/**
 * Date utility functions for consistent date handling across the application.
 * All dates from LA court data should be treated as LA timezone dates.
 */

/**
 * Parse an ISO date string (YYYY-MM-DD) as a local date without timezone conversion.
 * This prevents the off-by-one day issue when dates are interpreted as UTC.
 *
 * @param {string} dateString - ISO date string in format "YYYY-MM-DD"
 * @returns {Date|null} - Local Date object or null if invalid
 */
export const parseISODateAsLocal = (dateString) => {
  if (!dateString) return null;

  try {
    // Split the ISO date string and create a local date
    const [year, month, day] = dateString.split('-').map(Number);

    // Validate the parsed values
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      return null;
    }

    // Create a local date (months are 0-based in JavaScript)
    return new Date(year, month - 1, day);
  } catch (error) {
    console.warn('Failed to parse ISO date:', dateString, error);
    return null;
  }
};

/**
 * Parse a MM/DD/YYYY date string as a local date.
 * Used for future proceedings dates and other LA court date formats.
 *
 * @param {string} dateString - Date string in format "MM/DD/YYYY"
 * @returns {Date|null} - Local Date object or null if invalid
 */
export const parseSlashDateAsLocal = (dateString) => {
  if (!dateString) return null;

  try {
    // Split the date string and create a local date
    const [month, day, year] = dateString.split('/').map(Number);

    // Validate the parsed values
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      return null;
    }

    // Create a local date (months are 0-based in JavaScript)
    return new Date(year, month - 1, day);
  } catch (error) {
    console.warn('Failed to parse slash date:', dateString, error);
    return null;
  }
};

/**
 * Format a date string for display, handling both ISO and slash formats.
 *
 * @param {string} dateString - Date string in ISO (YYYY-MM-DD) or slash (MM/DD/YYYY) format
 * @param {string} fallback - Fallback text if date is invalid (default: 'N/A')
 * @returns {string} - Formatted date string or fallback
 */
export const formatDateForDisplay = (dateString, fallback = 'N/A') => {
  if (!dateString) return fallback;

  let date = null;

  // Try ISO format first (for openDate, closeDate)
  if (dateString.includes('-')) {
    date = parseISODateAsLocal(dateString);
  }
  // Try slash format (for future proceedings)
  else if (dateString.includes('/')) {
    date = parseSlashDateAsLocal(dateString);
  }

  if (!date || isNaN(date.getTime())) {
    return fallback;
  }

  return date.toLocaleDateString();
};

/**
 * Format a date string for display with specific locale options.
 *
 * @param {string} dateString - Date string in ISO (YYYY-MM-DD) or slash (MM/DD/YYYY) format
 * @param {object} options - Intl.DateTimeFormat options
 * @param {string} fallback - Fallback text if date is invalid (default: 'N/A')
 * @returns {string} - Formatted date string or fallback
 */
export const formatDateWithOptions = (
  dateString,
  options = {},
  fallback = 'N/A'
) => {
  if (!dateString) return fallback;

  let date = null;

  // Try ISO format first (for openDate, closeDate)
  if (dateString.includes('-')) {
    date = parseISODateAsLocal(dateString);
  }
  // Try slash format (for future proceedings)
  else if (dateString.includes('/')) {
    date = parseSlashDateAsLocal(dateString);
  }

  if (!date || isNaN(date.getTime())) {
    return fallback;
  }

  return date.toLocaleDateString('en-US', options);
};

/**
 * Format a date-only string (YYYY-MM-DD) without timezone conversion
 * This prevents off-by-one errors when displaying dates
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {string} Formatted date string (MM/DD/YYYY) or null if invalid
 */
export const formatDateOnly = (dateString) => {
  if (!dateString) return null;

  // Parse YYYY-MM-DD format directly without timezone conversion
  const parts = dateString.split('-');
  if (parts.length !== 3) return null;

  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);

  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;

  // Format as MM/DD/YYYY
  return `${month}/${day}/${year}`;
};
